import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-[#12121f] border border-[#2a2a3e] text-white placeholder:text-[#7a7a8a] focus:outline-none focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed]/40 transition-colors text-sm';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const formData = new FormData(e.currentTarget);
    const email = (formData.get('email') as string).trim();
    const password = formData.get('password') as string;

    setLoading(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (signInError) {
      setError('メールアドレスまたはパスワードが正しくありません。');
      return;
    }
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-[#0d0d1a] text-white flex flex-col relative overflow-hidden">
      <div
        className="blob w-[500px] h-[500px] -top-40 -right-40"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
      ></div>

      <div className="relative z-10 flex items-center justify-center flex-1 px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
              <span className="w-9 h-9 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#a855f7] flex items-center justify-center">
                <i className="ri-sparkling-2-line text-white text-lg"></i>
              </span>
              <span className="text-lg font-extrabold text-white">Luvira Admin</span>
            </Link>
            <h1 className="mt-6 text-2xl font-extrabold text-white">管理画面ログイン</h1>
            <p className="mt-2 text-sm text-[#a0a0b0]">メールアドレスとパスワードを入力してください。</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-8 space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                メールアドレス
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                className={inputClass}
                placeholder="admin@luvira.co.jp"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                パスワード
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                className={inputClass}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-12 w-full text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 hover:scale-[1.02] hover:shadow-[0_14px_30px_rgba(124,58,237,0.4)] disabled:opacity-60 cursor-pointer"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  認証中...
                </>
              ) : (
                'ログイン'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-[#a0a0b0] hover:text-[#a78bfa] transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> サイトトップへ戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}